// Sample data constants for the example cohort
import { EXAMPLE_COHORT_ID } from './utils';

export const SAMPLE_RECOMMENDATIONS = [
  "[SAMPLE] Consider recruiting more senior mentors in the marketing domain to match the 3 unmatched marketing mentees.",
  "[SAMPLE] Several mentees expressed interest in data science. Adding data science workshops could complement the mentorship program.",
  "[SAMPLE] The matching quality could improve by collecting more detailed information about mentees' specific career goals and challenges."
];

export const SAMPLE_UNMATCHED_MENTEES = [
  { mentee_name: "Sample Mentee F", reason: "No mentors available with expertise in blockchain technology" },
  { mentee_name: "Sample Mentee G", reason: "No sufficiently compatible mentors found in available pool" },
  { mentee_name: "Sample Mentee H", reason: "Seeking mentor in legal tech, no mentors in this domain" }
];

export const SAMPLE_UNMATCHED_MENTORS = [
  { mentor_name: "Sample Mentor C", reason: "No compatible mentees found matching expertise areas" },
  { mentor_name: "Sample Mentor D", reason: "Expertise in niche domain with no matching mentees" }
];

export const SAMPLE_DISTRIBUTION_METRICS = {
  average_mentees_per_mentor: 1.27,
  load_balance_score: 0.85,
  standard_deviation: 0.65,
  mentors_with_0_mentees: 2,
  mentors_with_1_mentee: 5,
  mentors_with_2_mentees: 3,
  mentors_with_3_mentees: 1,
  mentors_with_4_mentees: 0,
  mentors_with_5_mentees: 0
};

// Example cohort object
export const EXAMPLE_COHORT = {
  id: EXAMPLE_COHORT_ID,
  title: '📚 Example Cohort (Demo)',
  description: 'This is a sample cohort showing how AI matching works. Switch to a real cohort to see your actual data.',
  createdAt: new Date('2024-01-01'),
  status: 'active' as const,
  users: ['sample_user_1', 'sample_user_2', 'sample_user_3'],
  actions: [
    {
      action: 'selectParticipants' as const,
      status: 'completed' as const,
      createdAt: new Date('2024-01-02'),
      completedAt: new Date('2024-01-02')
    },
    {
      action: 'welcomePackage' as const,
      status: 'completed' as const,
      createdAt: new Date('2024-01-02'),
      completedAt: new Date('2024-01-02')
    },
    {
      action: 'createMatches' as const,
      status: 'completed' as const,
      createdAt: new Date('2024-01-03'),
      completedAt: new Date('2024-01-03')
    },
    {
      action: 'sendMatches' as const,
      status: 'completed' as const,
      createdAt: new Date('2024-01-04'),
      completedAt: new Date('2024-01-04')
    },
    {
      action: 'sendCheckIn' as const,
      status: 'completed' as const,
      createdAt: new Date('2024-01-04'),
      completedAt: new Date('2024-01-05')
    },
    {
      action: 'collectFeedback' as const,
      status: 'active' as const,
      createdAt: new Date('2024-01-05'),
      metadata: {
        scheduledAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Exactly 24 hours from now as string
        totalEmails: '10',
        triggeredBy: 'feedbackEmailGenerate' // Legacy reference for sample data
      }
    }
  ],
  connectionEmailExample: {
    recipients: ['[mentee.email]', '[mentor.email]'],
    subject: '[Tenant Name]: [labels.connection] introduction',
    content: `Hi [Mentee Name] and [Mentor Name],

I'm excited to introduce you two! I believe you'd make a great [labels.connection] match and have a lot to gain from connecting with each other.

[Mentee Name] is a [mentee.role] at [mentee.company] with interests in [mentee.interests]. [Mentor Name] is a [mentor.role] at [mentor.company] who has experience in [mentor.expertise].

I think you're a great match because [match.reasons] – you share common interests and complementary backgrounds that could lead to a meaningful [labels.mentorship] relationship.

I encourage you both to reach out and set up a time to connect. Looking forward to hearing how your [labels.connection] goes!

Latte
AI assistant facilitating [labels.mentorship] connections at [Tenant Name]`
  },
  feedbackEmailExample: {
    to: '[user.email]',
    subject: '[Tenant Name]: We\'d love your feedback',
    content: `Hi [User Name],

Thank you so much for participating in our [labels.mentorship] program! We hope you've had a valuable experience connecting with your [labels.mentor/mentee] match.

We'd love to hear about your experience! We have a quick survey for you to complete, and we'd also appreciate if you could reply directly to this email with your thoughts.

Please take a moment to reply to this email with your feedback on the following questions:

• How likely are you to recommend this program? (0-10)
• Did you meet with your [labels.mentor]/[labels.mentee] match after the introduction? (Yes/No)
• If yes, how did you meet? (e.g., In-Person, Video Call, Phone Call)
• If you met, how many days after the introduction did you meet?
• Any additional feedback or suggestions?

Additionally, please complete our external feedback survey here:
[customFeedbackContent - e.g., survey link or form]

Your honest feedback helps us improve the program for everyone. We truly appreciate you taking the time to share your thoughts!

Latte
AI assistant at [Tenant Name]`
  },
  welcomePackageEmailExample: {
    to: '[user.email]',
    subject: 'Welcome to [Tenant Name]\'s [labels.mentorship] program!',
    content: `Hi [User Name],

Welcome to [Tenant Name]'s [labels.mentorship] program! We're thrilled to have you join us as a [labels.mentee/mentor].

Here's what you can expect from the program:
• You'll be matched with a [labels.mentor/mentee] based on your goals and interests
• You'll receive an introduction email once your match is confirmed
• We encourage regular check-ins and open communication with your match

To help you get started, please review our welcome materials:
[customWelcomePackageContent - e.g., onboarding link or resources]

Keep an eye on your inbox for your match introduction coming soon. We're excited for you to begin this journey!

Latte
AI assistant at [Tenant Name]`
  },
  checkInEmailExample: {
    recipients: ['[user.email]'],
    subject: '[Tenant Name]: How is your [labels.mentorship] going?',
    content: `Hi [User Name],

We hope your [labels.mentorship] experience with [Tenant Name] is going well! We wanted to check in and see how things are progressing with your [labels.mentor/mentee] match.

A few questions we'd love to hear your thoughts on:
• Have you been able to connect with your [labels.mentor/mentee]?
• Are there any challenges or blockers we can help with?
• Is there anything we can do to make this experience better for you?

Feel free to reply directly to this email with any feedback, questions, or concerns. We're here to help make your [labels.mentorship] experience as valuable as possible!

Latte
AI assistant at [Tenant Name]`
  },
  welcomePackageEmailExampleMentor: {
    to: '[user.email]',
    subject: 'Welcome to [Tenant Name]\'s [labels.mentorship] program!',
    content: `Hi [User Name],

Welcome to [Tenant Name]'s [labels.mentorship] program! We're thrilled to have you join us as a [labels.mentor].

Here's what you can expect from the program:
• You'll be matched with a [labels.mentee] based on their goals and your expertise
• You'll receive an introduction email once your match is confirmed
• We encourage regular check-ins and open communication with your [labels.mentee]

To help you get started, please review our [labels.mentor] onboarding materials:
[customWelcomePackageContent - e.g., onboarding link or resources]

Keep an eye on your inbox for your match introduction coming soon. Thank you for sharing your time and experience — we're excited for you to begin this journey!

Latte
AI assistant at [Tenant Name]`
  },
  feedbackEmailExampleMentor: {
    to: '[user.email]',
    subject: '[Tenant Name]: We\'d love your feedback',
    content: `Hi [User Name],

Thank you so much for being a [labels.mentor] in our [labels.mentorship] program! We hope you've had a rewarding experience working with your [labels.mentee] match.

We'd love to hear about your experience as a [labels.mentor]! We have a quick survey for you to complete, and we'd also appreciate if you could reply directly to this email with your thoughts.

Please take a moment to reply to this email with your feedback on the following questions:

• How likely are you to recommend this program to other [labels.mentor]s? (0-10)
• Did you meet with your [labels.mentee] match after the introduction? (Yes/No)
• If yes, how did you meet? (e.g., In-Person, Video Call, Phone Call)
• If you met, how many days after the introduction did you meet?
• Any additional feedback or suggestions for how we can better support [labels.mentor]s?

Additionally, please complete our external feedback survey here:
[customFeedbackContent - e.g., survey link or form]

Your honest feedback helps us improve the program for everyone. Thank you for your time and commitment as a [labels.mentor]!

Latte
AI assistant at [Tenant Name]`
  },
  checkInEmailExampleMentor: {
    recipients: ['[user.email]'],
    subject: '[Tenant Name]: How is your [labels.mentorship] going?',
    content: `Hi [User Name],

We hope your [labels.mentorship] experience with [Tenant Name] is going well! We wanted to check in and see how things are progressing with your [labels.mentee] match.

A few questions we'd love to hear your thoughts on:
• Have you been able to connect with your [labels.mentee]?
• How are you finding the experience as a [labels.mentor]?
• Are there any challenges or blockers we can help with?
• Is there anything we can do to better support you as a [labels.mentor]?

Feel free to reply directly to this email with any feedback, questions, or concerns. Thank you for the time and energy you're putting into your [labels.mentee]'s growth!

Latte
AI assistant at [Tenant Name]`
  }
};

// Sample pending pairs data matching distribution: 5 mentors with 1 mentee, 3 mentors with 2 mentees, 1 mentor with 3 mentees
export const SAMPLE_PAIRS = [
  // Mentor A (1 mentee)
  {
    id: 'sample_1',
    mentee: {
      uid: 'mentee_a',
      firstName: 'Alex',
      lastName: 'Chen',
      email: 'alex.chen@example.com',
      role: 'Junior Product Manager',
      quote: 'Passionate about building products that make a difference',
      bio: 'Recent UC Berkeley grad working as a PM. Looking to connect with fellow Cal alumni in product leadership to learn and grow my career.',
      skills: ['Product Management', 'User Research', 'Roadmap Planning', 'Agile'],
      interests: ['AI/ML', 'Design Thinking', 'Product Strategy', 'User Experience'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'Toronto', state: 'ON', country: 'Canada' },
      educations: [
        { school: 'UC Berkeley', degree: 'BS', field: 'Computer Science', graduationYear: 2022 }
      ],
      works: [
        {
          company: 'TechStart Inc',
          title: 'Associate Product Manager',
          startDate: '2022-06',
          endDate: 'Present',
          description: 'Managing user onboarding flows and growth initiatives'
        }
      ],
      questionResponses: [
        { question: 'What are you hoping to get out of this mentorship?', response: 'As a recent Berkeley grad, I want to learn from experienced Cal alumni about navigating a product career and making strategic decisions.' },
        { question: 'What challenges are you currently facing?', response: 'Balancing stakeholder requests with user needs, and knowing when to say no to feature requests.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/alexchen' }
      ],
      createdAt: new Date('2024-01-15')
    },
    mentor: {
      uid: 'mentor_a',
      firstName: 'Sarah',
      lastName: 'Williams',
      email: 'sarah.williams@example.com',
      role: 'Senior Product Director',
      quote: 'Great products come from understanding people, not just technology',
      bio: 'Product leader with 12+ years building consumer and enterprise products. I\'ve led teams from 5 to 50 people and helped launch several successful products from 0 to 1.',
      skills: ['Product Strategy', 'Team Leadership', 'Go-to-Market', 'Product Vision', 'Stakeholder Management'],
      interests: ['AI Products', 'Mentoring', 'Product Leadership', 'Scaling Teams'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'New York', state: 'NY', country: 'USA' },
      educations: [
        { school: 'Stanford University', degree: 'MBA', field: 'Business', graduationYear: 2015 },
        { school: 'UC Berkeley', degree: 'BS', field: 'Engineering', graduationYear: 2008 }
      ],
      works: [
        {
          company: 'Enterprise Solutions Corp',
          title: 'Director of Product',
          startDate: '2019-03',
          endDate: 'Present',
          description: 'Leading product organization of 40+ people across multiple product lines'
        },
        {
          company: 'Growth Tech',
          title: 'Senior Product Manager',
          startDate: '2015-01',
          endDate: '2019-02',
          description: 'Led flagship product from 100K to 2M users'
        }
      ],
      questionResponses: [
        { question: 'What do you enjoy most about mentoring?', response: 'Helping people grow their careers and seeing them tackle challenges I once faced. There\'s nothing better than watching someone have that "aha" moment.' },
        { question: 'What areas can you provide guidance in?', response: 'Product strategy, stakeholder management, career growth, building and leading teams, and navigating organizational dynamics.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/sarahwilliams' },
        { type: 'Website', url: 'https://sarahwilliams.com' }
      ],
      createdAt: new Date('2023-11-20')
    },
    matchReason: 'Alex is a recent UC Berkeley grad looking to grow into a leadership role in product management. Sarah is also a Cal alumna who has spent over a decade leading product teams. Both share the Berkeley connection and a passion for understanding how technology solves real problems. Sarah has helped several junior colleagues advance into senior positions and is eager to give back to fellow Cal alumni. This alumni connection provides a strong foundation for mentorship around career development in product.',
    matchScore: { compatibility_score: 0.82 },
    status: 'pending' as const
  },

  // Mentor B (1 mentee)
  {
    id: 'sample_2',
    mentee: {
      uid: 'mentee_b',
      firstName: 'Jordan',
      lastName: 'Martinez',
      email: 'jordan.martinez@example.com',
      role: 'Software Engineer',
      quote: 'Love building elegant interfaces and learning new technologies',
      bio: 'Frontend engineer and recent Georgia Tech grad. Looking to connect with GT alumni to learn about backend development and career growth in engineering.',
      skills: ['JavaScript', 'React', 'TypeScript', 'Frontend Development', 'CSS'],
      interests: ['System Design', 'Scalability', 'Full Stack Development', 'Cloud Architecture'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'Vancouver', state: 'BC', country: 'Canada' },
      educations: [
        { school: 'Georgia Tech', degree: 'BS', field: 'Computer Science', graduationYear: 2021 }
      ],
      works: [
        {
          company: 'WebApp Solutions',
          title: 'Frontend Engineer',
          startDate: '2021-08',
          endDate: 'Present',
          description: 'Building user-facing features for a SaaS platform'
        }
      ],
      questionResponses: [
        { question: 'What are you hoping to learn?', response: 'I want to understand how different parts of a system work together and learn best practices for building scalable applications.' },
        { question: 'What are your career goals?', response: 'Become a senior engineer in the next few years and eventually move into a tech lead role.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/jordanmartinez' },
        { type: 'GitHub', url: 'https://github.com/jordanmartinez' }
      ],
      createdAt: new Date('2024-01-18')
    },
    mentor: {
      uid: 'mentor_b',
      firstName: 'Michael',
      lastName: 'Thompson',
      email: 'michael.thompson@example.com',
      role: 'Engineering Manager',
      quote: 'Great systems are built by great teams',
      bio: 'Engineering manager with 10 years of experience building distributed systems. I love teaching and helping engineers grow their technical and leadership skills.',
      skills: ['Distributed Systems', 'Cloud Architecture', 'Technical Leadership', 'Mentoring', 'System Design'],
      interests: ['Backend Engineering', 'Mentoring Engineers', 'Team Building', 'Technical Strategy'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'Seattle', state: 'WA', country: 'USA' },
      educations: [
        { school: 'Carnegie Mellon University', degree: 'MS', field: 'Computer Science', graduationYear: 2013 },
        { school: 'Georgia Tech', degree: 'BS', field: 'Computer Science', graduationYear: 2011 }
      ],
      works: [
        {
          company: 'Cloud Innovations',
          title: 'Engineering Manager',
          startDate: '2018-06',
          endDate: 'Present',
          description: 'Leading team of 12 engineers building cloud infrastructure platform'
        },
        {
          company: 'ScaleUp Tech',
          title: 'Senior Software Engineer',
          startDate: '2013-09',
          endDate: '2018-05',
          description: 'Built backend services handling millions of requests per day'
        }
      ],
      questionResponses: [
        { question: 'What do you enjoy about mentoring?', response: 'Helping engineers level up their skills and watching them solve complex problems. I love mentoring fellow Georgia Tech alumni and helping them grow their careers.' },
        { question: 'What guidance can you provide?', response: 'Technical architecture, career growth in engineering, transitioning to leadership, and navigating complex technical challenges.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/michaelthompson' }
      ],
      createdAt: new Date('2023-10-15')
    },
    matchReason: 'Jordan is a recent Georgia Tech grad working in frontend who wants to expand into backend and system design. Michael is also a Georgia Tech alum with a Master\'s in CS who now manages an engineering team. The shared GT connection creates a strong foundation for mentorship. Michael has experience across the stack and enjoys helping fellow Yellow Jackets level up their technical skills and navigate their engineering careers. This alumni pairing offers great opportunities for learning system architecture and career advancement.',
    matchScore: { compatibility_score: 0.78 },
    status: 'pending' as const
  },

  // Mentor E (1 mentee)
  {
    id: 'sample_3',
    mentee: {
      uid: 'mentee_e',
      firstName: 'Taylor',
      lastName: 'Johnson',
      email: 'taylor.johnson@example.com',
      role: 'UX Designer',
      quote: 'Design is not just what it looks like, it\'s how it works for everyone',
      bio: 'UX designer passionate about accessibility and inclusive design. I want to grow into a design leadership role and learn how to build design systems at scale.',
      skills: ['UI Design', 'Prototyping', 'User Testing', 'Figma', 'Accessibility'],
      interests: ['Design Systems', 'Accessibility', 'Design Leadership', 'User Research'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'Portland', state: 'OR', country: 'USA' },
      educations: [
        { school: 'Rhode Island School of Design', degree: 'BFA', field: 'Graphic Design', graduationYear: 2020 }
      ],
      works: [
        {
          company: 'Design Forward',
          title: 'UX Designer',
          startDate: '2020-09',
          endDate: 'Present',
          description: 'Designing mobile and web experiences with focus on accessibility'
        }
      ],
      questionResponses: [
        { question: 'What are your career aspirations?', response: 'I want to lead design teams and be known for creating inclusive, accessible products that work for everyone.' },
        { question: 'What challenges do you face?', response: 'Balancing design ideals with business constraints, and learning how to advocate for accessibility when it\'s not prioritized.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/taylorjohnson' },
        { type: 'Portfolio', url: 'https://taylorjohnson.design' }
      ],
      createdAt: new Date('2024-01-20')
    },
    mentor: {
      uid: 'mentor_e',
      firstName: 'Emma',
      lastName: 'Rodriguez',
      email: 'emma.rodriguez@example.com',
      role: 'Head of Design',
      quote: 'Inclusive design isn\'t a nice-to-have, it\'s essential',
      bio: 'Design leader with 15 years of experience. I\'ve built design teams from scratch and established design systems used by millions. Passionate about accessibility and mentoring designers.',
      skills: ['Design Systems', 'Design Leadership', 'Product Design', 'Team Building', 'Accessibility'],
      interests: ['Accessibility', 'Inclusive Design', 'Design Operations', 'Mentoring'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'San Francisco', state: 'CA', country: 'USA' },
      educations: [
        { school: 'Parsons School of Design', degree: 'MFA', field: 'Design', graduationYear: 2009 },
        { school: 'UCLA', degree: 'BA', field: 'Psychology', graduationYear: 2006 }
      ],
      works: [
        {
          company: 'Design Leaders Inc',
          title: 'Head of Design',
          startDate: '2020-01',
          endDate: 'Present',
          description: 'Leading design organization of 30+ designers and researchers'
        },
        {
          company: 'Product Design Co',
          title: 'Senior Design Manager',
          startDate: '2015-06',
          endDate: '2019-12',
          description: 'Built design system used across 50+ product teams'
        }
      ],
      questionResponses: [
        { question: 'Why do you mentor?', response: 'I want to help designers avoid the mistakes I made and grow faster in their careers. Design needs more leaders who prioritize accessibility.' },
        { question: 'What can you help with?', response: 'Design leadership, building design systems, advocating for accessibility, career growth, and managing design teams.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/emmarodriguez' },
        { type: 'Website', url: 'https://emmarodriguez.com' }
      ],
      createdAt: new Date('2023-09-10')
    },
    matchReason: 'Taylor is a designer who cares deeply about creating experiences that work for everyone. They have good skills in creating prototypes and testing with users. Emma leads a design organization and has experience building design frameworks used across teams. Both are passionate about making products accessible and inclusive, which creates a strong foundation for this mentorship around shared values and career growth in design leadership.',
    matchScore: { compatibility_score: 0.85 },
    status: 'pending' as const
  },

  // Mentor F (1 mentee)
  {
    id: 'sample_4',
    mentee: {
      uid: 'mentee_f',
      firstName: 'Morgan',
      lastName: 'Lee',
      email: 'morgan.lee@example.com',
      role: 'Data Analyst',
      quote: 'Turning data into actionable insights',
      bio: 'Northwestern alum working as a data analyst. Eager to connect with fellow Wildcats in data science to learn advanced ML techniques and transition my career.',
      skills: ['SQL', 'Python', 'Data Visualization', 'Tableau', 'Statistics'],
      interests: ['Machine Learning', 'Predictive Analytics', 'Data Science', 'AI'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'Montreal', state: 'QC', country: 'Canada' },
      educations: [
        { school: 'Northwestern University', degree: 'BS', field: 'Statistics', graduationYear: 2021 }
      ],
      works: [
        {
          company: 'Analytics Hub',
          title: 'Data Analyst',
          startDate: '2021-07',
          endDate: 'Present',
          description: 'Creating dashboards and reports to help business make data-driven decisions'
        }
      ],
      questionResponses: [
        { question: 'What are you looking to learn?', response: 'Advanced machine learning techniques and how to apply them to real business problems. Also want to understand how data science teams operate.' },
        { question: 'Where do you want your career to go?', response: 'Transition into a data scientist role and eventually lead a data science team.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/morganlee' },
        { type: 'GitHub', url: 'https://github.com/morganlee' }
      ],
      createdAt: new Date('2024-01-22')
    },
    mentor: {
      uid: 'mentor_f',
      firstName: 'David',
      lastName: 'Kim',
      email: 'david.kim@example.com',
      role: 'Senior Data Scientist',
      quote: 'Data science is about asking the right questions',
      bio: 'Data scientist with 8 years of experience applying ML to solve business problems. I\'ve built models that generated millions in value and love teaching others about data science.',
      skills: ['Machine Learning', 'Statistical Modeling', 'Python', 'Deep Learning', 'A/B Testing'],
      interests: ['ML Applications', 'Data-Driven Strategy', 'Mentoring', 'AI Ethics'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'Boston', state: 'MA', country: 'USA' },
      educations: [
        { school: 'MIT', degree: 'PhD', field: 'Computer Science', graduationYear: 2015 },
        { school: 'Northwestern University', degree: 'BS', field: 'Mathematics', graduationYear: 2009 }
      ],
      works: [
        {
          company: 'AI Solutions Corp',
          title: 'Senior Data Scientist',
          startDate: '2018-03',
          endDate: 'Present',
          description: 'Leading ML initiatives and building predictive models for customer behavior'
        },
        {
          company: 'Data Insights',
          title: 'Data Scientist',
          startDate: '2015-06',
          endDate: '2018-02',
          description: 'Built recommendation systems and forecasting models'
        }
      ],
      questionResponses: [
        { question: 'What do you enjoy about mentoring?', response: 'Helping people transition into data science and teaching them how to think about problems in a data-driven way.' },
        { question: 'What areas can you provide guidance in?', response: 'Machine learning, statistical modeling, career transition into data science, and applying ML to business problems.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/davidkim' },
        { type: 'Medium', url: 'https://medium.com/@davidkim' }
      ],
      createdAt: new Date('2023-08-25')
    },
    matchReason: 'Morgan is a Northwestern alum working as a data analyst who wants to transition into data science and learn advanced ML techniques. David is also a Northwestern Wildcat who went on to get his PhD at MIT and now works as a senior data scientist. The shared Northwestern connection creates an immediate bond. David has successfully made the transition from mathematics to data science and loves helping fellow Wildcats advance their careers. This alumni pairing offers excellent mentorship for someone looking to break into data science.',
    matchScore: { compatibility_score: 0.79 },
    status: 'pending' as const
  },

  // Mentor G (1 mentee)
  {
    id: 'sample_5',
    mentee: {
      uid: 'mentee_g',
      firstName: 'Casey',
      lastName: 'Brown',
      email: 'casey.brown@example.com',
      role: 'Marketing Coordinator',
      quote: 'Great marketing tells stories that resonate',
      bio: 'Marketing professional focused on content creation and social media. Want to learn about growth marketing and strategic campaign planning to advance my career.',
      skills: ['Content Marketing', 'Social Media', 'Analytics', 'Copywriting', 'SEO'],
      interests: ['Growth Marketing', 'Digital Strategy', 'Brand Building', 'Marketing Analytics'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'Denver', state: 'CO', country: 'USA' },
      educations: [
        { school: 'University of Colorado', degree: 'BA', field: 'Marketing', graduationYear: 2022 }
      ],
      works: [
        {
          company: 'Marketing Pros',
          title: 'Marketing Coordinator',
          startDate: '2022-06',
          endDate: 'Present',
          description: 'Managing social media channels and creating content for campaigns'
        }
      ],
      questionResponses: [
        { question: 'What are your goals?', response: 'Move into a marketing manager role and learn how to develop comprehensive marketing strategies that drive business growth.' },
        { question: 'What challenges do you face?', response: 'Understanding how to measure marketing ROI and prioritize initiatives that have the biggest impact.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/caseybrown' }
      ],
      createdAt: new Date('2024-01-25')
    },
    mentor: {
      uid: 'mentor_g',
      firstName: 'Lisa',
      lastName: 'Anderson',
      email: 'lisa.anderson@example.com',
      role: 'VP Marketing',
      quote: 'Marketing is about understanding people and solving their problems',
      bio: 'Marketing executive with 12 years scaling marketing at high-growth companies. I\'ve built marketing teams and led campaigns that generated 10x ROI.',
      skills: ['Growth Strategy', 'Brand Development', 'Team Management', 'Marketing Analytics', 'Go-to-Market'],
      interests: ['Data-Driven Marketing', 'Scaling Teams', 'Growth Hacking', 'Mentoring'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'Los Angeles', state: 'CA', country: 'USA' },
      educations: [
        { school: 'UCLA', degree: 'MBA', field: 'Marketing', graduationYear: 2012 },
        { school: 'USC', degree: 'BA', field: 'Communications', graduationYear: 2008 }
      ],
      works: [
        {
          company: 'Growth Ventures',
          title: 'VP Marketing',
          startDate: '2019-01',
          endDate: 'Present',
          description: 'Leading marketing organization of 25+ people across brand, growth, and content'
        },
        {
          company: 'ScaleUp Marketing',
          title: 'Director of Marketing',
          startDate: '2014-06',
          endDate: '2018-12',
          description: 'Built marketing function from ground up and grew company 5x'
        }
      ],
      questionResponses: [
        { question: 'Why do you mentor?', response: 'I want to help marketers grow faster than I did and avoid common pitfalls. Marketing is evolving so quickly, and I love sharing what I\'ve learned.' },
        { question: 'What can you help with?', response: 'Marketing strategy, growth tactics, team building, measuring marketing ROI, and career advancement in marketing.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/lisaanderson' },
        { type: 'Twitter', url: 'https://twitter.com/lisaanderson' }
      ],
      createdAt: new Date('2023-07-18')
    },
    matchReason: 'Casey is a marketing professional who creates content and manages social channels, and wants to understand the bigger picture of marketing strategy. They are curious about how to measure impact and grow their career. Lisa has led marketing efforts at growing companies and can share lessons on building teams and developing strategy. Their different experience levels and complementary interests make this a solid pairing for professional growth.',
    matchScore: { compatibility_score: 0.76 },
    status: 'pending' as const
  },

  // Mentor H (2 mentees)
  {
    id: 'sample_6',
    mentee: {
      uid: 'mentee_h1',
      firstName: 'Sam',
      lastName: 'Davis',
      email: 'sam.davis@example.com',
      role: 'Junior Developer',
      quote: 'Learning to code, one bug at a time',
      bio: 'Career-changer from business to software development. Recently completed a coding bootcamp and looking to connect with experienced engineers from Stanford to learn and grow.',
      skills: ['Python', 'Django', 'HTML/CSS', 'JavaScript', 'Git'],
      interests: ['Web Development', 'APIs', 'Software Engineering', 'Clean Code'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'Calgary', state: 'AB', country: 'Canada' },
      educations: [
        { school: 'Coding Bootcamp', degree: 'Certificate', field: 'Web Development', graduationYear: 2023 },
        { school: 'Stanford University', degree: 'BA', field: 'Business', graduationYear: 2020 }
      ],
      works: [
        {
          company: 'Web Solutions',
          title: 'Junior Developer',
          startDate: '2023-09',
          endDate: 'Present',
          description: 'Building features for web applications and fixing bugs'
        }
      ],
      questionResponses: [
        { question: 'What do you want to learn?', response: 'Best practices for writing clean, maintainable code and how to debug complex issues effectively.' },
        { question: 'What are your career goals?', response: 'Become a confident mid-level engineer within 2 years and eventually specialize in backend development.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/samdavis' },
        { type: 'GitHub', url: 'https://github.com/samdavis' }
      ],
      createdAt: new Date('2024-02-01')
    },
    mentor: {
      uid: 'mentor_h',
      firstName: 'James',
      lastName: 'Wilson',
      email: 'james.wilson@example.com',
      role: 'Principal Engineer',
      quote: 'Great code is code that other people can understand and maintain',
      bio: 'Principal engineer with 15+ years building web applications and distributed systems. I love teaching and have mentored over 20 engineers in my career.',
      skills: ['Full Stack Development', 'System Architecture', 'Python', 'JavaScript', 'Mentoring'],
      interests: ['Teaching', 'Web Technologies', 'Software Craftsmanship', 'Code Review'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'San Francisco', state: 'CA', country: 'USA' },
      educations: [
        { school: 'Stanford University', degree: 'MS', field: 'Computer Science', graduationYear: 2009 },
        { school: 'UC Berkeley', degree: 'BS', field: 'EECS', graduationYear: 2007 }
      ],
      works: [
        {
          company: 'Tech Giants Inc',
          title: 'Principal Engineer',
          startDate: '2018-01',
          endDate: 'Present',
          description: 'Leading technical architecture and mentoring senior engineers'
        },
        {
          company: 'StartupCo',
          title: 'Senior Software Engineer',
          startDate: '2010-06',
          endDate: '2017-12',
          description: 'Built core platform from scratch and scaled to millions of users'
        }
      ],
      questionResponses: [
        { question: 'What do you love about mentoring?', response: 'Watching junior engineers grow and helping them avoid the mistakes I made early in my career. There\'s nothing better than seeing someone have a breakthrough moment.' },
        { question: 'What can you teach?', response: 'Software design, debugging techniques, code review, system architecture, and how to grow as an engineer.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/jameswilson' },
        { type: 'GitHub', url: 'https://github.com/jameswilson' }
      ],
      createdAt: new Date('2023-06-10')
    },
    matchReason: 'Sam is a Stanford alum who career-changed from business into software development after completing a coding bootcamp. They\'re eager to learn from experienced engineers. James is also a Stanford alum (with an MS in CS) who has mentored over 20 engineers throughout his career. The shared Stanford connection creates a strong foundation for mentorship. James appreciates Sam\'s business background and can help bridge technical skills with product thinking. This alumni pairing offers excellent support for a career-changer navigating their first engineering role.',
    matchScore: { compatibility_score: 0.81 },
    status: 'pending' as const
  },
  {
    id: 'sample_7',
    mentee: {
      uid: 'mentee_h2',
      firstName: 'Riley',
      lastName: 'Garcia',
      email: 'riley.garcia@example.com',
      role: 'DevOps Engineer',
      quote: 'Automating everything, one script at a time',
      bio: 'DevOps engineer passionate about infrastructure and automation. Want to learn more about system architecture and cloud-native technologies.',
      skills: ['Docker', 'CI/CD', 'Linux', 'AWS', 'Kubernetes'],
      interests: ['Cloud Infrastructure', 'Automation', 'Site Reliability', 'System Design'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'Atlanta', state: 'GA', country: 'USA' },
      educations: [
        { school: 'Georgia Tech', degree: 'BS', field: 'Computer Science', graduationYear: 2020 }
      ],
      works: [
        {
          company: 'Cloud Services Inc',
          title: 'DevOps Engineer',
          startDate: '2020-08',
          endDate: 'Present',
          description: 'Managing deployment pipelines and cloud infrastructure'
        }
      ],
      questionResponses: [
        { question: 'What are you hoping to learn?', response: 'How to design reliable, scalable systems and understand the tradeoffs in different architectural approaches.' },
        { question: 'Career aspirations?', response: 'Move into a platform engineering or SRE role and eventually lead infrastructure teams.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/rileygarcia' },
        { type: 'GitHub', url: 'https://github.com/rileygarcia' }
      ],
      createdAt: new Date('2024-02-03')
    },
    mentor: {
      uid: 'mentor_h',
      firstName: 'James',
      lastName: 'Wilson',
      email: 'james.wilson@example.com',
      role: 'Principal Engineer',
      quote: 'Great code is code that other people can understand and maintain',
      bio: 'Principal engineer with 15+ years building web applications and distributed systems. I love teaching and have mentored over 20 engineers in my career.',
      skills: ['Full Stack Development', 'System Architecture', 'Python', 'JavaScript', 'Mentoring'],
      interests: ['Teaching', 'Web Technologies', 'Software Craftsmanship', 'Code Review'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'San Francisco', state: 'CA', country: 'USA' },
      educations: [
        { school: 'Stanford University', degree: 'MS', field: 'Computer Science', graduationYear: 2009 },
        { school: 'UC Berkeley', degree: 'BS', field: 'EECS', graduationYear: 2007 }
      ],
      works: [
        {
          company: 'Tech Giants Inc',
          title: 'Principal Engineer',
          startDate: '2018-01',
          endDate: 'Present',
          description: 'Leading technical architecture and mentoring senior engineers'
        },
        {
          company: 'StartupCo',
          title: 'Senior Software Engineer',
          startDate: '2010-06',
          endDate: '2017-12',
          description: 'Built core platform from scratch and scaled to millions of users'
        }
      ],
      questionResponses: [
        { question: 'What do you love about mentoring?', response: 'Watching junior engineers grow and helping them avoid the mistakes I made early in my career. There\'s nothing better than seeing someone have a breakthrough moment.' },
        { question: 'What can you teach?', response: 'Software design, debugging techniques, code review, system architecture, and how to grow as an engineer.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/jameswilson' },
        { type: 'GitHub', url: 'https://github.com/jameswilson' }
      ],
      createdAt: new Date('2023-06-10')
    },
    matchReason: 'Riley works on deployment and infrastructure and wants to expand their understanding of how systems are designed. They have experience with tools and automation and are interested in learning about architecture. James has built and maintained large systems and can share insights on designing reliable infrastructure. This mentorship can help the mentee see the bigger picture of how infrastructure supports products.',
    matchScore: { compatibility_score: 0.77 },
    status: 'pending' as const
  },

  // Mentor I (2 mentees)
  {
    id: 'sample_8',
    mentee: {
      uid: 'mentee_i1',
      firstName: 'Drew',
      lastName: 'Taylor',
      email: 'drew.taylor@example.com',
      role: 'Sales Development Rep',
      quote: 'Every no brings me closer to a yes',
      bio: 'Recent Boston University grad working as an SDR. Looking to connect with fellow BU alumni in sales to learn about closing enterprise deals and advancing my career.',
      skills: ['B2B Sales', 'Lead Generation', 'CRM', 'Cold Outreach', 'Sales Prospecting'],
      interests: ['Enterprise Sales', 'Sales Strategy', 'Negotiation', 'Deal Closing'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'Dallas', state: 'TX', country: 'USA' },
      educations: [
        { school: 'Boston University', degree: 'BA', field: 'Business', graduationYear: 2022 }
      ],
      works: [
        {
          company: 'Sales Solutions Corp',
          title: 'Sales Development Rep',
          startDate: '2022-07',
          endDate: 'Present',
          description: 'Generating qualified leads and setting meetings for sales team'
        }
      ],
      questionResponses: [
        { question: 'What do you want to learn?', response: 'How to qualify prospects effectively and understand the full sales cycle from prospecting to close.' },
        { question: 'Career goals?', response: 'Become an Account Executive within a year and eventually move into sales leadership.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/drewtaylor' }
      ],
      createdAt: new Date('2024-02-05')
    },
    mentor: {
      uid: 'mentor_i',
      firstName: 'Jennifer',
      lastName: 'Moore',
      email: 'jennifer.moore@example.com',
      role: 'Director of Sales',
      quote: 'Sales is about solving problems, not pushing products',
      bio: 'Sales leader with 10+ years closing enterprise deals and building sales organizations. I\'ve helped numerous SDRs transition into successful AE roles.',
      skills: ['Enterprise Sales', 'Sales Strategy', 'Deal Negotiation', 'Team Building', 'Pipeline Management'],
      interests: ['Team Leadership', 'Sales Enablement', 'Coaching', 'Sales Strategy'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'Boston', state: 'MA', country: 'USA' },
      educations: [
        { school: 'Boston University', degree: 'BA', field: 'Business Administration', graduationYear: 2012 }
      ],
      works: [
        {
          company: 'Enterprise Sales Inc',
          title: 'Director of Sales',
          startDate: '2020-03',
          endDate: 'Present',
          description: 'Leading sales organization of 35+ reps, consistently hitting 120%+ of quota'
        },
        {
          company: 'Growth Corp',
          title: 'Senior Account Executive',
          startDate: '2015-01',
          endDate: '2020-02',
          description: 'Top performer, closed over $10M in new business'
        }
      ],
      questionResponses: [
        { question: 'Why do you mentor?', response: 'I remember how hard it was starting in sales, and I want to help people succeed faster than I did. Watching someone close their first big deal is incredibly rewarding.' },
        { question: 'What can you help with?', response: 'Sales process, deal strategy, qualification, negotiation, transitioning from SDR to AE, and career growth in sales.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/jennifermoore' }
      ],
      createdAt: new Date('2023-05-15')
    },
    matchReason: 'Drew is a recent Boston University grad working as an SDR who wants to transition into an Account Executive role. Jennifer is also a BU alum who has built a successful sales career and now leads a sales organization. The shared Terrier connection creates an immediate bond. Jennifer has helped numerous SDRs make the transition to AE roles and is passionate about mentoring fellow BU alumni. This alumni pairing offers excellent guidance on advancing in sales and learning enterprise deal strategy.',
    matchScore: { compatibility_score: 0.80 },
    status: 'pending' as const
  },
  {
    id: 'sample_9',
    mentee: {
      uid: 'mentee_i2',
      firstName: 'Quinn',
      lastName: 'Adams',
      email: 'quinn.adams@example.com',
      role: 'Account Manager',
      quote: 'Building relationships that last',
      bio: 'Account manager focused on customer success and retention. Want to learn how to identify upsell opportunities and grow accounts strategically.',
      skills: ['Customer Success', 'Account Management', 'Relationship Building', 'CRM', 'Account Planning'],
      interests: ['Customer Retention', 'Upselling', 'Strategic Account Management', 'Business Development'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'Nashville', state: 'TN', country: 'USA' },
      educations: [
        { school: 'Vanderbilt University', degree: 'BA', field: 'Communications', graduationYear: 2021 }
      ],
      works: [
        {
          company: 'Customer First Inc',
          title: 'Account Manager',
          startDate: '2021-08',
          endDate: 'Present',
          description: 'Managing portfolio of 30+ accounts and ensuring customer satisfaction'
        }
      ],
      questionResponses: [
        { question: 'What are you looking to learn?', response: 'How to identify expansion opportunities and have strategic conversations with customers about growing our partnership.' },
        { question: 'Where do you see your career going?', response: 'Move into strategic account management or customer success leadership.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/quinnadams' }
      ],
      createdAt: new Date('2024-02-08')
    },
    mentor: {
      uid: 'mentor_i',
      firstName: 'Jennifer',
      lastName: 'Moore',
      email: 'jennifer.moore@example.com',
      role: 'Director of Sales',
      quote: 'Sales is about solving problems, not pushing products',
      bio: 'Sales leader with 10+ years closing enterprise deals and building sales organizations. I\'ve helped numerous SDRs transition into successful AE roles.',
      skills: ['Enterprise Sales', 'Sales Strategy', 'Deal Negotiation', 'Team Building', 'Pipeline Management'],
      interests: ['Team Leadership', 'Sales Enablement', 'Coaching', 'Sales Strategy'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'Boston', state: 'MA', country: 'USA' },
      educations: [
        { school: 'Boston University', degree: 'BA', field: 'Business Administration', graduationYear: 2012 }
      ],
      works: [
        {
          company: 'Enterprise Sales Inc',
          title: 'Director of Sales',
          startDate: '2020-03',
          endDate: 'Present',
          description: 'Leading sales organization of 35+ reps, consistently hitting 120%+ of quota'
        },
        {
          company: 'Growth Corp',
          title: 'Senior Account Executive',
          startDate: '2015-01',
          endDate: '2020-02',
          description: 'Top performer, closed over $10M in new business'
        }
      ],
      questionResponses: [
        { question: 'Why do you mentor?', response: 'I remember how hard it was starting in sales, and I want to help people succeed faster than I did. Watching someone close their first big deal is incredibly rewarding.' },
        { question: 'What can you help with?', response: 'Sales process, deal strategy, qualification, negotiation, transitioning from SDR to AE, and career growth in sales.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/jennifermoore' }
      ],
      createdAt: new Date('2023-05-15')
    },
    matchReason: 'Quinn manages customer relationships and wants to learn how to identify growth opportunities within existing accounts. They are good at building connections with clients and want to develop more strategic skills. Jennifer can share insights on account planning and deal structuring from years of sales experience. This mentorship offers perspective on how to think strategically about customer relationships and grow within sales roles.',
    matchScore: { compatibility_score: 0.74 },
    status: 'pending' as const
  },

  // Mentor J (2 mentees)
  {
    id: 'sample_10',
    mentee: {
      uid: 'mentee_j1',
      firstName: 'Avery',
      lastName: 'White',
      email: 'avery.white@example.com',
      role: 'HR Coordinator',
      quote: 'People are the heart of every organization',
      bio: 'HR professional passionate about creating great workplace cultures. Want to move into strategic HR and learn about organizational development.',
      skills: ['Recruiting', 'HR Operations', 'Employee Relations', 'Onboarding', 'HRIS'],
      interests: ['People Strategy', 'Organizational Culture', 'HR Leadership', 'Talent Development'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'Philadelphia', state: 'PA', country: 'USA' },
      educations: [
        { school: 'Penn State', degree: 'BA', field: 'Human Resources', graduationYear: 2021 }
      ],
      works: [
        {
          company: 'People First Corp',
          title: 'HR Coordinator',
          startDate: '2021-09',
          endDate: 'Present',
          description: 'Supporting recruiting efforts and managing employee onboarding'
        }
      ],
      questionResponses: [
        { question: 'What are your goals?', response: 'Move into a strategic HR role where I can impact organizational culture and help develop talent strategies.' },
        { question: 'What challenges are you facing?', response: 'Understanding how to measure HR impact on business outcomes and learning to think more strategically about people initiatives.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/averywhite' }
      ],
      createdAt: new Date('2024-02-10')
    },
    mentor: {
      uid: 'mentor_j',
      firstName: 'Patricia',
      lastName: 'Harris',
      email: 'patricia.harris@example.com',
      role: 'VP of People',
      quote: 'Culture isn\'t what you say, it\'s what you do',
      bio: 'People leader with 14 years building HR functions at high-growth companies. I\'ve scaled organizations from 50 to 500+ people while maintaining strong cultures.',
      skills: ['HR Strategy', 'Organizational Development', 'Executive Leadership', 'Culture Building', 'Talent Management'],
      interests: ['Culture Building', 'Leadership Development', 'Coaching', 'People Analytics'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'New York', state: 'NY', country: 'USA' },
      educations: [
        { school: 'Cornell University', degree: 'MS', field: 'Industrial Relations', graduationYear: 2010 },
        { school: 'NYU', degree: 'BA', field: 'Psychology', graduationYear: 2007 }
      ],
      works: [
        {
          company: 'Growth Leaders Inc',
          title: 'VP of People',
          startDate: '2019-06',
          endDate: 'Present',
          description: 'Leading people function for 500+ person organization across 10 countries'
        },
        {
          company: 'ScaleUp HR',
          title: 'Director of HR',
          startDate: '2014-03',
          endDate: '2019-05',
          description: 'Built HR function from scratch and scaled team from 2 to 15 people'
        }
      ],
      questionResponses: [
        { question: 'Why mentor?', response: 'HR was my second career, so I know how hard it can be to break in and grow. I want to help people accelerate their HR careers and build better workplaces.' },
        { question: 'What can you help with?', response: 'HR strategy, organizational design, culture building, talent development, and career growth in people roles.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/patriciaharris' }
      ],
      createdAt: new Date('2023-04-20')
    },
    matchReason: 'Avery works in HR and wants to move beyond day-to-day operations into more strategic people work. They care about creating positive workplace environments and want to understand how HR can drive organizational success. Patricia has led people functions at growing organizations and can share lessons on building culture and developing HR strategy. Both share a passion for workplace culture, making this a strong foundation for mentorship about HR leadership.',
    matchScore: { compatibility_score: 0.83 },
    status: 'pending' as const
  },
  {
    id: 'sample_11',
    mentee: {
      uid: 'mentee_j2',
      firstName: 'Blake',
      lastName: 'Miller',
      email: 'blake.miller@example.com',
      role: 'Technical Recruiter',
      quote: 'Finding the right talent is like solving a puzzle',
      bio: 'Technical recruiter specializing in engineering roles. Want to learn about talent strategy and how recruiting fits into broader business objectives.',
      skills: ['Technical Recruiting', 'Sourcing', 'Candidate Assessment', 'Boolean Search', 'ATS'],
      interests: ['Talent Strategy', 'Employer Branding', 'Recruiting Operations', 'Diversity & Inclusion'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'San Jose', state: 'CA', country: 'USA' },
      educations: [
        { school: 'San Jose State', degree: 'BA', field: 'Business', graduationYear: 2020 }
      ],
      works: [
        {
          company: 'Tech Talent Partners',
          title: 'Technical Recruiter',
          startDate: '2020-07',
          endDate: 'Present',
          description: 'Recruiting engineers for fast-growing tech companies'
        }
      ],
      questionResponses: [
        { question: 'What do you want to learn?', response: 'How to build recruiting programs that attract top talent and understand how recruiting impacts business outcomes.' },
        { question: 'Career aspirations?', response: 'Lead recruiting for a tech company and eventually move into a head of talent or recruiting operations role.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/blakemiller' }
      ],
      createdAt: new Date('2024-02-12')
    },
    mentor: {
      uid: 'mentor_j',
      firstName: 'Patricia',
      lastName: 'Harris',
      email: 'patricia.harris@example.com',
      role: 'VP of People',
      quote: 'Culture isn\'t what you say, it\'s what you do',
      bio: 'People leader with 14 years building HR functions at high-growth companies. I\'ve scaled organizations from 50 to 500+ people while maintaining strong cultures.',
      skills: ['HR Strategy', 'Organizational Development', 'Executive Leadership', 'Culture Building', 'Talent Management'],
      interests: ['Culture Building', 'Leadership Development', 'Coaching', 'People Analytics'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'New York', state: 'NY', country: 'USA' },
      educations: [
        { school: 'Cornell University', degree: 'MS', field: 'Industrial Relations', graduationYear: 2010 },
        { school: 'NYU', degree: 'BA', field: 'Psychology', graduationYear: 2007 }
      ],
      works: [
        {
          company: 'Growth Leaders Inc',
          title: 'VP of People',
          startDate: '2019-06',
          endDate: 'Present',
          description: 'Leading people function for 500+ person organization across 10 countries'
        },
        {
          company: 'ScaleUp HR',
          title: 'Director of HR',
          startDate: '2014-03',
          endDate: '2019-05',
          description: 'Built HR function from scratch and scaled team from 2 to 15 people'
        }
      ],
      questionResponses: [
        { question: 'Why mentor?', response: 'HR was my second career, so I know how hard it can be to break in and grow. I want to help people accelerate their HR careers and build better workplaces.' },
        { question: 'What can you help with?', response: 'HR strategy, organizational design, culture building, talent development, and career growth in people roles.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/patriciaharris' }
      ],
      createdAt: new Date('2023-04-20')
    },
    matchReason: 'Blake recruits candidates and wants to understand how recruiting fits into broader talent and company strategy. They are good at finding people and want to learn how to build recruiting programs that attract top talent. Patricia has built recruiting functions and can share perspective on employer branding and strategic hiring. This pairing can help the mentee think more strategically about their recruiting career.',
    matchScore: { compatibility_score: 0.75 },
    status: 'pending' as const
  },

  // Mentor K (3 mentees)
  {
    id: 'sample_12',
    mentee: {
      uid: 'mentee_k1',
      firstName: 'Reese',
      lastName: 'Campbell',
      email: 'reese.campbell@example.com',
      role: 'Business Analyst',
      quote: 'Data tells the story, but analysis reveals the insight',
      bio: 'UChicago alum working as a business analyst. Looking to connect with fellow Maroons who have made the transition into strategy or consulting roles.',
      skills: ['Data Analysis', 'Business Intelligence', 'Excel', 'SQL', 'Business Modeling'],
      interests: ['Strategic Planning', 'Business Strategy', 'Corporate Finance', 'Management Consulting'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'Chicago', state: 'IL', country: 'USA' },
      educations: [
        { school: 'University of Chicago', degree: 'BA', field: 'Economics', graduationYear: 2022 }
      ],
      works: [
        {
          company: 'Strategy Analytics',
          title: 'Business Analyst',
          startDate: '2022-07',
          endDate: 'Present',
          description: 'Analyzing business performance and supporting strategic decision-making'
        }
      ],
      questionResponses: [
        { question: 'What do you want to learn?', response: 'How to think strategically about business problems and develop frameworks for making complex decisions.' },
        { question: 'Career goals?', response: 'Move into a strategy role, either in corporate strategy or management consulting.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/reesecampbell' }
      ],
      createdAt: new Date('2024-02-15')
    },
    mentor: {
      uid: 'mentor_k',
      firstName: 'Robert',
      lastName: 'Jackson',
      email: 'robert.jackson@example.com',
      role: 'VP of Strategy',
      quote: 'Strategy is about making choices and living with the consequences',
      bio: 'Strategy executive with 16 years leading strategic planning at Fortune 500 companies. Former management consultant and UChicago alum, now focused on mentoring the next generation of strategists.',
      skills: ['Business Strategy', 'Strategic Planning', 'Financial Analysis', 'M&A', 'Corporate Development'],
      interests: ['Mentoring Multiple People', 'Strategic Thinking', 'Business Transformation', 'Leadership Development'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'Chicago', state: 'IL', country: 'USA' },
      educations: [
        { school: 'Harvard Business School', degree: 'MBA', field: 'Strategy', graduationYear: 2008 },
        { school: 'University of Chicago', degree: 'BA', field: 'Economics', graduationYear: 2003 }
      ],
      works: [
        {
          company: 'Global Strategy Corp',
          title: 'VP of Strategy',
          startDate: '2018-01',
          endDate: 'Present',
          description: 'Leading corporate strategy and M&A for $5B company'
        },
        {
          company: 'McKinsey & Company',
          title: 'Senior Associate',
          startDate: '2010-06',
          endDate: '2017-12',
          description: 'Strategy consultant advising C-suite on growth and transformation'
        }
      ],
      questionResponses: [
        { question: 'Why mentor?', response: 'I\'ve been fortunate to learn from great mentors in my career, and I want to pay it forward. I find great joy in helping people develop strategic thinking skills.' },
        { question: 'What can you help with?', response: 'Strategic frameworks, business analysis, career transitions into strategy or consulting, and leadership development.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/robertjackson' }
      ],
      createdAt: new Date('2023-03-10')
    },
    matchReason: 'Reese is a recent UChicago grad in Economics working as a business analyst who wants to transition into strategy or consulting. Robert is also a UChicago Maroon in Economics who went on to Harvard Business School and spent years at McKinsey before leading corporate strategy. The shared Chicago connection creates a strong foundation. Robert successfully made the transition from analysis to strategy and loves helping fellow Maroons navigate career pivots. This alumni pairing offers excellent mentorship for breaking into strategy or consulting.',
    matchScore: { compatibility_score: 0.79 },
    status: 'pending' as const
  },
  {
    id: 'sample_13',
    mentee: {
      uid: 'mentee_k2',
      firstName: 'Cameron',
      lastName: 'Parker',
      email: 'cameron.parker@example.com',
      role: 'Operations Manager',
      quote: 'Efficiency is doing things right, effectiveness is doing the right things',
      bio: 'Operations manager focused on process improvement. Want to develop strategic thinking and understand how operations connects to overall business strategy.',
      skills: ['Process Improvement', 'Project Management', 'Operations', 'Six Sigma', 'Supply Chain'],
      interests: ['Operational Efficiency', 'Business Transformation', 'Strategic Operations', 'Change Management'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'Houston', state: 'TX', country: 'USA' },
      educations: [
        { school: 'UT Austin', degree: 'BS', field: 'Industrial Engineering', graduationYear: 2019 }
      ],
      works: [
        {
          company: 'Operations Excellence Inc',
          title: 'Operations Manager',
          startDate: '2019-07',
          endDate: 'Present',
          description: 'Managing operations for manufacturing facility, implemented process improvements that reduced costs by 15%'
        }
      ],
      questionResponses: [
        { question: 'What are you hoping to learn?', response: 'How to think more strategically about operations and understand how operational decisions impact business outcomes.' },
        { question: 'Career aspirations?', response: 'Move into a COO track or operations consulting role where I can drive business transformation.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/cameronparker' }
      ],
      createdAt: new Date('2024-02-18')
    },
    mentor: {
      uid: 'mentor_k',
      firstName: 'Robert',
      lastName: 'Jackson',
      email: 'robert.jackson@example.com',
      role: 'VP of Strategy',
      quote: 'Strategy is about making choices and living with the consequences',
      bio: 'Strategy executive with 16 years leading strategic planning at Fortune 500 companies. Former management consultant and UChicago alum, now focused on mentoring the next generation of strategists.',
      skills: ['Business Strategy', 'Strategic Planning', 'Financial Analysis', 'M&A', 'Corporate Development'],
      interests: ['Mentoring Multiple People', 'Strategic Thinking', 'Business Transformation', 'Leadership Development'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'Chicago', state: 'IL', country: 'USA' },
      educations: [
        { school: 'Harvard Business School', degree: 'MBA', field: 'Strategy', graduationYear: 2008 },
        { school: 'University of Chicago', degree: 'BA', field: 'Economics', graduationYear: 2003 }
      ],
      works: [
        {
          company: 'Global Strategy Corp',
          title: 'VP of Strategy',
          startDate: '2018-01',
          endDate: 'Present',
          description: 'Leading corporate strategy and M&A for $5B company'
        },
        {
          company: 'McKinsey & Company',
          title: 'Senior Associate',
          startDate: '2010-06',
          endDate: '2017-12',
          description: 'Strategy consultant advising C-suite on growth and transformation'
        }
      ],
      questionResponses: [
        { question: 'Why mentor?', response: 'I\'ve been fortunate to learn from great mentors in my career, and I want to pay it forward. I find great joy in helping people develop strategic thinking skills.' },
        { question: 'What can you help with?', response: 'Strategic frameworks, business analysis, career transitions into strategy or consulting, and leadership development.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/robertjackson' }
      ],
      createdAt: new Date('2023-03-10')
    },
    matchReason: 'Cameron manages operations and wants to develop a more strategic mindset. They have improved several processes and want to understand how operations connects to company strategy. Robert can share insights on strategic thinking and long-term planning from a leadership perspective. While their day-to-day work differs, this cross-functional pairing offers valuable learning about how different parts of an organization work together.',
    matchScore: { compatibility_score: 0.71 },
    status: 'pending' as const
  },
  {
    id: 'sample_14',
    mentee: {
      uid: 'mentee_k3',
      firstName: 'Skyler',
      lastName: 'Evans',
      email: 'skyler.evans@example.com',
      role: 'Finance Analyst',
      quote: 'Numbers don\'t lie, but they do tell stories',
      bio: 'UChicago Economics grad working as a finance analyst. Looking to connect with fellow Maroons in strategic finance or corporate development roles to learn and advance my career.',
      skills: ['Financial Modeling', 'Financial Analysis', 'Excel', 'FP&A', 'Financial Reporting'],
      interests: ['Corporate Finance', 'Strategic Finance', 'M&A', 'Business Strategy'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'New York', state: 'NY', country: 'USA' },
      educations: [
        { school: 'University of Chicago', degree: 'BS', field: 'Economics', graduationYear: 2022 }
      ],
      works: [
        {
          company: 'Financial Services Corp',
          title: 'Finance Analyst',
          startDate: '2022-08',
          endDate: 'Present',
          description: 'Supporting FP&A team with budgeting, forecasting, and financial analysis'
        }
      ],
      questionResponses: [
        { question: 'What do you want to learn?', response: 'How financial analysis supports strategic decision-making and how to think about the intersection of finance and strategy.' },
        { question: 'Career goals?', response: 'Move into strategic finance or corporate development, eventually CFO track.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/skylerevans' }
      ],
      createdAt: new Date('2024-02-20')
    },
    mentor: {
      uid: 'mentor_k',
      firstName: 'Robert',
      lastName: 'Jackson',
      email: 'robert.jackson@example.com',
      role: 'VP of Strategy',
      quote: 'Strategy is about making choices and living with the consequences',
      bio: 'Strategy executive with 16 years leading strategic planning at Fortune 500 companies. Former management consultant and UChicago alum, now focused on mentoring the next generation of strategists.',
      skills: ['Business Strategy', 'Strategic Planning', 'Financial Analysis', 'M&A', 'Corporate Development'],
      interests: ['Mentoring Multiple People', 'Strategic Thinking', 'Business Transformation', 'Leadership Development'],
      professional: true,
      profileImageUrl: '',
      locationData: { city: 'Chicago', state: 'IL', country: 'USA' },
      educations: [
        { school: 'Harvard Business School', degree: 'MBA', field: 'Strategy', graduationYear: 2008 },
        { school: 'University of Chicago', degree: 'BA', field: 'Economics', graduationYear: 2003 }
      ],
      works: [
        {
          company: 'Global Strategy Corp',
          title: 'VP of Strategy',
          startDate: '2018-01',
          endDate: 'Present',
          description: 'Leading corporate strategy and M&A for $5B company'
        },
        {
          company: 'McKinsey & Company',
          title: 'Senior Associate',
          startDate: '2010-06',
          endDate: '2017-12',
          description: 'Strategy consultant advising C-suite on growth and transformation'
        }
      ],
      questionResponses: [
        { question: 'Why mentor?', response: 'I\'ve been fortunate to learn from great mentors in my career, and I want to pay it forward. I find great joy in helping people develop strategic thinking skills.' },
        { question: 'What can you help with?', response: 'Strategic frameworks, business analysis, career transitions into strategy or consulting, and leadership development.' }
      ],
      links: [
        { type: 'LinkedIn', url: 'https://linkedin.com/in/robertjackson' }
      ],
      createdAt: new Date('2023-03-10')
    },
    matchReason: 'Skyler is a recent UChicago Economics grad working in finance who wants to move into strategic finance or corporate development. Robert is also a UChicago Maroon in Economics who has spent his career at the intersection of finance and strategy, including M&A work. The shared Chicago connection and economics background create an excellent foundation. Robert can provide insights on how financial analysis informs strategic decisions and help Skyler navigate a transition into strategic finance. This alumni pairing offers great mentorship for advancing from FP&A into corporate strategy roles.',
    matchScore: { compatibility_score: 0.77 },
    status: 'pending' as const
  }
];
